import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationCountGateway,
  type NotificationCountGateway,
} from '../notifications/notification-count-gateway';
import { useSession } from '../session/SessionContext';
import {
  subscribeToRows,
  tableRowsChannel,
  type RealtimeMessage,
  type Unsubscribe,
} from '../../services/appwrite/realtime';
import { useToast } from '../../shared/accessibility/Toast';

const NOTIFICATIONS_CHANNEL = tableRowsChannel('community', 'notifications');

interface NotificationEvent {
  $id?: string;
  userId?: string;
  text?: string;
}

export type RealtimeSubscribe = <T>(
  channels: string[],
  onMessage: (message: RealtimeMessage<T>) => void,
) => Unsubscribe;

interface RealtimeSyncProps {
  /** Suscripción alternativa para pruebas. */
  subscribe?: RealtimeSubscribe;
  /** Gateway alternativo para pruebas; `null` omite la comprobación de respaldo. */
  notificationGateway?: NotificationCountGateway | null;
}

/**
 * Abre la conexión Realtime al autenticar y la conserva durante toda la
 * sesión, con independencia del dominio o la ruta actual.
 */
export function RealtimeSync({ subscribe, notificationGateway: injectedGateway }: RealtimeSyncProps) {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const receivedNotifications = useRef(new Set<string>());
  const countGateway = injectedGateway === undefined
    ? (import.meta.env.MODE === 'test' ? null : notificationCountGateway)
    : injectedGateway;

  useQuery({
    queryKey: ['notification-count'],
    queryFn: () => countGateway?.count() ?? Promise.resolve(0),
    enabled: state.status === 'authenticated' && countGateway !== null,
    // Realtime sigue siendo el camino inmediato. Esto recupera el estado si
    // el navegador suspendió o perdió el WebSocket mientras estaba al fondo.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (state.status !== 'authenticated') return undefined;
    // Las pruebas generales de páginas no deben abrir sockets contra el
    // servidor real. Esta pieza se prueba inyectando su suscripción.
    const listen = subscribe ?? (import.meta.env.MODE === 'test' ? null : subscribeToRows);
    if (!listen) return undefined;
    const userId = state.profile.user.id;

    return listen<NotificationEvent>([NOTIFICATIONS_CHANNEL], (event) => {
      const payload = event.payload ?? {};
      if (payload.userId && payload.userId !== userId) return;

      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-count'] });

      const created = (event.events ?? []).some((name) => name.endsWith('.create'));
      if (created && payload.text) {
        if (payload.$id && receivedNotifications.current.has(payload.$id)) return;
        if (payload.$id) receivedNotifications.current.add(payload.$id);
        showToast({
          title: 'Nueva notificación',
          description: payload.text,
          tone: 'info',
          timeout: 10_000,
        });
      }
    });
  }, [queryClient, showToast, state, subscribe]);

  return null;
}
